import React, { useState } from "react";
import { Shield, Lock, Eye, EyeOff, Loader2, AlertTriangle, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useFinancePin } from "@/context/FinancePinContext";
import PlanLock from "@/components/PlanLock";

/**
 * SecurityPinSettings — Settings → PIN economico.
 *
 * Mirrors the iPhone passcode UX (per spec):
 *   • plan ineligible          → upsell card
 *   • PIN not configured       → simple "Salva PIN" form (PIN + confirm)
 *   • PIN configured           → three actions:
 *       - Modifica PIN     (asks the account password)
 *       - Disattiva PIN    (asks the account password)
 *       - Ho dimenticato il PIN  (asks the account password + new PIN)
 *
 * The account password is used ONLY to recover, change or disable the
 * PIN. It is never accepted as an unlock substitute on Dashboard /
 * AI Manager — that's the whole point of the feature.
 */

function PinDigits({ value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        inputMode="numeric"
        autoComplete="off"
        pattern="\d*"
        maxLength={6}
        value={value}
        onChange={(e) => onChange((e.target.value || "").replace(/\D/g, "").slice(0, 6))}
        className="rounded-sm pr-10 tabular-nums tracking-[0.4em]"
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 transition-colors"
        aria-label={show ? "Nascondi PIN" : "Mostra PIN"}
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
      </button>
    </div>
  );
}

const validPin = (p) => /^\d{4,6}$/.test(p);

export default function SecurityPinSettings() {
  const { enabled, planEligible, refreshStatus } = useFinancePin();
  const [busy, setBusy] = useState(false);

  // Setup form (no PIN yet)
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");

  // Modal states
  const [changeOpen, setChangeOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  // Shared "auth + new pin" form (used by change / forgot)
  const [authPwd, setAuthPwd] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPinConfirm, setNewPinConfirm] = useState("");
  const [disablePwd, setDisablePwd] = useState("");

  const resetSharedForm = () => { setAuthPwd(""); setNewPin(""); setNewPinConfirm(""); };

  // ── Setup ───────────────────────────────────────────────────────────
  const handleSetup = async () => {
    if (!validPin(pin)) { toast.error("Il PIN deve avere da 4 a 6 cifre."); return; }
    if (pin !== pinConfirm) { toast.error("I due PIN non coincidono."); return; }
    setBusy(true);
    try {
      await api.post("/settings/finance-pin/setup", { pin });
      toast.success("PIN economico salvato.");
      setPin(""); setPinConfirm("");
      await refreshStatus();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossibile salvare il PIN.");
    } finally { setBusy(false); }
  };

  // ── Change / Forgot (same backend endpoint) ────────────────────────
  const handleChangeOrForgot = async (closeFn) => {
    if (!authPwd) { toast.error("Inserisci la password del tuo account."); return; }
    if (!validPin(newPin)) { toast.error("Il nuovo PIN deve avere da 4 a 6 cifre."); return; }
    if (newPin !== newPinConfirm) { toast.error("I due PIN non coincidono."); return; }
    setBusy(true);
    try {
      await api.post("/settings/finance-pin/change", {
        current_password: authPwd,
        pin: newPin,
      });
      toast.success("PIN aggiornato.");
      resetSharedForm();
      closeFn(false);
      await refreshStatus();
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Password non corretta.");
      else toast.error("Impossibile aggiornare il PIN.");
    } finally { setBusy(false); }
  };

  // ── Disable ────────────────────────────────────────────────────────
  const handleDisable = async () => {
    if (!disablePwd) { toast.error("Inserisci la password per disattivare il PIN."); return; }
    setBusy(true);
    try {
      await api.delete("/settings/finance-pin", { data: { current_password: disablePwd } });
      toast.success("PIN disattivato.");
      setDisablePwd(""); setDisableOpen(false);
      await refreshStatus();
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Password non corretta.");
      else toast.error("Impossibile disattivare il PIN.");
    } finally { setBusy(false); }
  };

  // ── Plan gate ─────────────────────────────────────────────────────
  if (!planEligible) {
    return (
      <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-finance-pin-section">
        <h2 className="font-serif-heading text-2xl flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" strokeWidth={1.5} /> PIN economico
        </h2>
        <p className="text-sm text-neutral-500 mt-1 mb-5">
          Proteggi i dati economici di Dashboard e AI Manager con un PIN numerico.
        </p>
        <PlanLock module="finance-pin" requiredPlan="pro" label="PIN economico">
          <div />
        </PlanLock>
      </section>
    );
  }

  // ── PIN ACTIVE — three actions ────────────────────────────────────
  if (enabled) {
    return (
      <>
        <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-finance-pin-section">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif-heading text-2xl flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" strokeWidth={1.5} /> PIN economico
              </h2>
              <p className="text-sm text-neutral-500 mt-1">
                Attivo. I dati economici sono nascosti finché non sblocchi con il PIN dalla
                Dashboard o da AI Manager.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="finance-pin-status-active">
              <Shield className="w-3.5 h-3.5" strokeWidth={1.5} /> Attivo
            </span>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button type="button" variant="outline" className="rounded-sm"
                    onClick={() => { resetSharedForm(); setChangeOpen(true); }}
                    data-testid="finance-pin-change-open">
              Modifica PIN
            </Button>
            <Button type="button" variant="outline" className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50"
                    onClick={() => { setDisablePwd(""); setDisableOpen(true); }}
                    data-testid="finance-pin-disable-open">
              <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={1.5} /> Disattiva PIN
            </Button>
            <button
              type="button"
              className="text-sm text-primary hover:text-primary/80 underline-offset-4 hover:underline ml-auto self-center"
              onClick={() => { resetSharedForm(); setForgotOpen(true); }}
              data-testid="finance-pin-forgot-open"
            >
              Ho dimenticato il PIN
            </button>
          </div>
        </section>

        {/* Change PIN dialog */}
        <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
          <DialogContent className="max-w-md" data-testid="finance-pin-change-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif-heading text-xl">Modifica PIN</DialogTitle>
              <DialogDescription>
                Conferma la password del tuo account e scegli un nuovo PIN (4–6 cifre).
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 mt-2">
              <div>
                <Label className="label-uppercase">Password account</Label>
                <Input type="password" value={authPwd} onChange={(e) => setAuthPwd(e.target.value)}
                       autoComplete="current-password" className="mt-1 rounded-sm"
                       data-testid="finance-pin-change-pwd" />
              </div>
              <div>
                <Label className="label-uppercase">Nuovo PIN</Label>
                <PinDigits value={newPin} onChange={setNewPin} testid="finance-pin-change-new" />
              </div>
              <div>
                <Label className="label-uppercase">Conferma nuovo PIN</Label>
                <PinDigits value={newPinConfirm} onChange={setNewPinConfirm} testid="finance-pin-change-confirm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setChangeOpen(false)}>Annulla</Button>
              <Button onClick={() => handleChangeOrForgot(setChangeOpen)}
                      disabled={busy || !authPwd || !validPin(newPin) || newPin !== newPinConfirm}
                      data-testid="finance-pin-change-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Aggiorna PIN
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Forgot PIN dialog (same flow under a different label) */}
        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent className="max-w-md" data-testid="finance-pin-forgot-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif-heading text-xl flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-primary" strokeWidth={1.5} /> Recupera PIN
              </DialogTitle>
              <DialogDescription>
                Inserisci la password del tuo account per impostare un nuovo PIN.
                La password serve solo per modificare il PIN, mai per sbloccare i dati.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 mt-2">
              <div>
                <Label className="label-uppercase">Password account</Label>
                <Input type="password" value={authPwd} onChange={(e) => setAuthPwd(e.target.value)}
                       autoComplete="current-password" className="mt-1 rounded-sm"
                       data-testid="finance-pin-forgot-pwd" />
              </div>
              <div>
                <Label className="label-uppercase">Nuovo PIN</Label>
                <PinDigits value={newPin} onChange={setNewPin} testid="finance-pin-forgot-new" />
              </div>
              <div>
                <Label className="label-uppercase">Conferma nuovo PIN</Label>
                <PinDigits value={newPinConfirm} onChange={setNewPinConfirm} testid="finance-pin-forgot-confirm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setForgotOpen(false)}>Annulla</Button>
              <Button onClick={() => handleChangeOrForgot(setForgotOpen)}
                      disabled={busy || !authPwd || !validPin(newPin) || newPin !== newPinConfirm}
                      data-testid="finance-pin-forgot-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Imposta nuovo PIN
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Disable PIN dialog */}
        <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
          <DialogContent className="max-w-md" data-testid="finance-pin-disable-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif-heading text-xl">Disattiva PIN economico</DialogTitle>
              <DialogDescription>
                I dati economici torneranno visibili a chiunque acceda con il tuo account.
                Conferma con la password per procedere.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <Label className="label-uppercase">Password account</Label>
              <Input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
                     autoComplete="current-password" className="mt-1 rounded-sm"
                     data-testid="finance-pin-disable-pwd" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setDisableOpen(false)}>Annulla</Button>
              <Button onClick={handleDisable} disabled={busy || !disablePwd}
                      className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50" variant="outline"
                      data-testid="finance-pin-disable-submit">
                <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={1.5} /> Disattiva
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ── PIN NOT YET CONFIGURED — first-time setup ─────────────────────
  return (
    <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-finance-pin-section">
      <h2 className="font-serif-heading text-2xl flex items-center gap-2">
        <Lock className="w-5 h-5 text-primary" strokeWidth={1.5} /> PIN economico
      </h2>
      <p className="text-sm text-neutral-500 mt-1 mb-5">
        Imposta un PIN numerico (4–6 cifre) per proteggere fatturato, forecast e KPI economici.
        Il PIN si imposta una sola volta: dalla Dashboard o da AI Manager basterà toccare il
        piccolo lucchetto e digitarlo per visualizzare i numeri.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
        <div>
          <Label className="label-uppercase">PIN (4–6 cifre)</Label>
          <PinDigits value={pin} onChange={setPin} testid="finance-pin-setup-input" />
        </div>
        <div>
          <Label className="label-uppercase">Conferma PIN</Label>
          <PinDigits value={pinConfirm} onChange={setPinConfirm} testid="finance-pin-setup-confirm" />
        </div>
      </div>

      {pin && pinConfirm && pin !== pinConfirm ? (
        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
          I due PIN non coincidono.
        </p>
      ) : null}

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSetup} disabled={busy || !validPin(pin) || pin !== pinConfirm}
                className="rounded-sm" data-testid="finance-pin-setup-submit">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
          Salva PIN economico
        </Button>
      </div>
    </section>
  );
}
